import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import i18n from "./setup/i18n";
import createVuetify from "./setup/vuetify";
import "@socotec.io/socio-vue-components/style.css";
import { createOidc, useAuth, useOidcStore } from "vue3-oidc";
import { WebStorageStateStore } from "oidc-client-ts";

import SocioManifest from "../socio-manifest.json";
import PackageJson from "../package.json";
import SocioVueComponents, {
  initializeTracingAndLogging,
} from "@socotec.io/socio-vue-components";
import { createORM } from "pinia-orm";
import { createPinia } from "pinia";
import { oidcSettings } from "./setup/OIDCSettings";
import * as Sentry from "@sentry/vue";
import { getDefaultSentryConfig } from "./setup/sentryConfig";
import { stonlyIdentifyWidget } from "@/utils/stonlyIdentifyWidget";
import { createSentryPiniaPlugin } from "@sentry/vue";

let silentRenewErrorCount = 0;

const faroSettings = {
  enableTelemetry: import.meta.env.VITE_APP_ENABLE_TELEMETRY,
  grafanaApiKey: import.meta.env.VITE_APP_GRAFANA_API_KEY,
  collectorUrl: import.meta.env.VITE_APP_COLLECTOR_URL,
  propagateUrls: import.meta.env.VITE_APP_PROPAGATE_URLS,
  grpcApiEnv: import.meta.env.VITE_APP_SOCIO_GRPC_API_ENV,
  faroLogLevel: import.meta.env.VITE_APP_FARO_LOG_LEVEL,
};

initializeTracingAndLogging(SocioManifest, PackageJson, faroSettings, {
  router,
});
await createOidc({
  oidcSettings,
  auth: false,
  events: {
    addUserLoaded: (user) => {
      console.debug("Token renewed");
      useOidcStore().actions.value.setUser(user);
      silentRenewErrorCount = 0;
      stonlyIdentifyWidget(user.profile.email, user.profile.name);
      Sentry.setUser({
        id: user.profile.usermanagementUuid,
        email: user.profile.email,
      });
    },
    addAccessTokenExpiring: () => {
      console.debug("Token expiring soon");
    },
    addAccessTokenExpired: () => {
      console.log("Token expired");
    },
    addSilentRenewError: (error) => {
      silentRenewErrorCount++;
      console.error(
        `Silent renew failed (attempt ${silentRenewErrorCount})`,
        error,
      );
      const fatalErrors = [
        "login_required",
        "interaction_required",
        "session_not_found",
      ];
      if (silentRenewErrorCount >= 3 || fatalErrors.includes(error?.error)) {
        console.error("Silent renew failed permanently, logging out...");
        useAuth().signoutRedirect();
      }
    },
  },
  refreshToken: {
    enable: true,
  },
  userStore: new WebStorageStateStore({
    prefix: "vue3-oidc",
    store: globalThis.sessionStorage,
  }),
});

const app = createApp(App);
Sentry.init(
  getDefaultSentryConfig({
    app,
    dsn: import.meta.env.VITE_APP_SENTRY_DSN,
    router,
    release: `vigico@${import.meta.env.VITE_APP_VERSION}`,
    enabled: import.meta.env.MODE !== "development",
    environment: import.meta.env.VITE_APP_SOCIO_GRPC_API_ENV,
  }),
);

const pinia = createPinia().use(createORM());
pinia.use(createSentryPiniaPlugin());

app.use(router);
app.use(pinia);
app.use(i18n);
app.use(createVuetify(i18n));
app.use(SocioVueComponents, i18n);

app.mount("#app");
